import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { getSocketOrigin } from '../services/api';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

function resolveSocketUrl() {
  const explicit = process.env.REACT_APP_SOCKET_URL;
  if (explicit) {
    return String(explicit).trim().replace(/\/+$/, '');
  }
  return getSocketOrigin();
}

export const SocketProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setSocket(null);
      setConnected(false);
      return undefined;
    }

    const url = resolveSocketUrl();
    const socketInstance = io(url, {
      path: '/socket.io',
      // WebSocket first avoids broken long-poll through some proxies; polling remains as fallback
      transports: ['websocket', 'polling'],
      upgrade: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      autoConnect: true,
    });

    socketInstance.on('connect', () => {
      setConnected(true);
      const uid = user._id || user.id;
      if (uid) socketInstance.emit('join-user-room', uid);
    });

    socketInstance.on('disconnect', () => {
      setConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[socket] connect_error:', error?.message || error);
      }
      setConnected(false);
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.close();
      setConnected(false);
    };
  }, [isAuthenticated, user?.id]);

  const value = {
    socket,
    connected,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
